import type { ComponentDefinition } from "./types";

/** Only the supported single-pole positive DC arrangement can establish a
 * protective boundary. Malformed imported metadata must not hide an unfused
 * circuit. This validates topology, not manufacturer evidence or trip timing. */
export function dcBreakerSpec(def: ComponentDefinition) {
  const breaker = def.dcBreaker;
  if (!breaker || def.category !== "distribution" || def.isFuseBlock ||
      def.terminals.length !== 2 || !Number.isFinite(breaker.ratingA) || breaker.ratingA <= 0 ||
      breaker.inputTerminalId === breaker.outputTerminalId ||
      def.internalDcConnections?.length || def.oneWayDcConnections?.length) return undefined;
  const input = def.terminals.find(t => t.id === breaker.inputTerminalId);
  const output = def.terminals.find(t => t.id === breaker.outputTerminalId);
  if (input?.polarity !== "positive" || input.direction !== "input" ||
      output?.polarity !== "positive" || output.direction !== "output") return undefined;
  return breaker;
}

/** Published/declared evidence only. Nominal bus compatibility is not a DC
 * interruption rating, and an absent figure is never treated as a pass. */
export function dcBreakerInterruptA(def: ComponentDefinition, voltage: number): number | undefined {
  const breaker = dcBreakerSpec(def);
  if (!breaker || !Number.isFinite(voltage) || voltage <= 0 ||
      !Number.isFinite(breaker.dcMaxVoltage) || voltage > breaker.dcMaxVoltage! ||
      !Array.isArray(breaker.interruptRatings)) return undefined;
  return breaker.interruptRatings.filter(rating => Number.isFinite(rating.upToVoltage) &&
    Number.isFinite(rating.amperes) && rating.amperes > 0 && voltage <= rating.upToVoltage)
    .sort((a, b) => a.upToVoltage - b.upToVoltage)[0]?.amperes;
}
